import React, { useEffect, useState } from 'react';
import { Button, Modal, ModalBody, ModalHeader } from 'reactstrap';
import { format, isEqual, isToday, isYesterday, startOfDay } from 'date-fns';
import { useDispatch } from 'react-redux';
import * as Icon from 'react-feather';
import { cleanPhoneNumber, getFileExtension } from '../../../utils/common';
import PropTypes from 'prop-types';
import { getWhatsappReportHistory } from '../../../store/slices/tenantSlices/chatSlice';
import { userOptionGet } from '../../../store/slices/tenantSlices/agentPerformanceSlice';
import JsPDF from 'jspdf';

const WhatsappReportInfo = ({ setIsPreviewUrl, isOpen, handleCancel, details }) => {
  const dispatch = useDispatch();

  const [chatHistory, setChatHistory] = useState([]);
  const [allUserOptions, setAllUserOptions] = useState([]);

  const chatReportHistoryGet = async () => {
    try {
      const payload = {
        start_date: details?.from_date,
        end_date: details?.to_date,
        to_number: details?.to_number,
        whatsapp_number: details?.whatsapp_number
      };
      const response = await dispatch(getWhatsappReportHistory(payload)).unwrap();
      setChatHistory((response?.data || []).reverse());
    } catch (error) {
      console.log('error', error);
    }
  };

  const getUserOption = async () => {
    try {
      const response = await dispatch(userOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.Username || '-',
          value: val.id
        }));
        setAllUserOptions(options);
      } else {
        setAllUserOptions([]);
      }
    } catch (error) {
      setAllUserOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    if (isOpen) {
      chatReportHistoryGet();
      getUserOption();
    }
  }, [details, isOpen]);

  const getFileName = (path) => {
    const pathnames = path?.split('/');
    return pathnames?.[pathnames.length - 1];
  };

  const fileDownload = async (url) => {
    try {
      const response = await fetch(url, {
        mode: 'cors'
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;

      const filename = url.split('/').pop();
      link.download = filename || 'download';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const findUserName = (id) => {
    const data = allUserOptions?.find((val) => val?.value === Number(id));
    return data?.label;
  };

  const handleExportPDF = () => {
    const doc = new JsPDF();
    let y = 10;
    const lineHeight = 8;
    const pageHeight = 297;

    let lastDate = null;

    [...chatHistory]?.reverse().forEach((msg, idx) => {
      if (msg?.media) return;

      const msgDate = startOfDay(new Date(msg.timestamp));
      const isNewDay = !lastDate || !isEqual(lastDate, msgDate);

      if (isNewDay) {
        if (y + lineHeight * 2 > pageHeight - 20) {
          doc.addPage();
          y = 10;
        }

        const dateLabel = isToday(msgDate)
          ? 'Today'
          : isYesterday(msgDate)
            ? 'Yesterday'
            : format(msgDate, 'dd MMM yyyy');

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.setTextColor(0);
        doc.text(dateLabel, 10, y);
        y += lineHeight + 2;

        lastDate = msgDate;
      }

      const time = format(new Date(msg.timestamp), 'hh:mm a');
      const sender = cleanPhoneNumber(msg?.from_whatsapp_number);

      let content = '';

      if (['transformed', 'Type', 'Status', 'assign'].includes(msg?.action)) {
        if (msg?.action === 'transformed' || msg?.action === 'assign') {
          content = `Ticket transformed${msg?.old_type !== 'system' ? ` from ${findUserName(Number(msg?.old_type))}` : ` from ${msg?.old_type}`} to ${msg?.new_type}`;
        } else if (msg?.action === 'Type') {
          content = `Ticket Status changed ${msg?.old_type} to ${msg?.new_type}`;
        } else {
          content = `Ticket Type changed ${msg?.old_type} to ${msg?.new_type}`;
        }
      } else {
        content = `${msg.message.replace(/\r?\n/g, ' ')}`;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(30, 30, 30);
      if (!['transformed', 'Type', 'Status', 'assign'].includes(msg?.action)) {
        doc.text(`${sender}:`, 10, y);
      }

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(50, 50, 50);
      doc.text(content, 35, y);

      doc.setFontSize(9);
      doc.setTextColor(150);
      if (!['transformed', 'Type', 'Status', 'assign'].includes(msg?.action)) {
        doc.text(time, 180, y);
      }

      y += lineHeight;

      if (y > pageHeight - 20) {
        doc.addPage();
        y = 10;
      }
    });

    const footerNote = 'Note: Media files (images, PDFs, etc.) are excluded from this export.';
    if (y + lineHeight > pageHeight - 10) {
      doc.addPage();
      y = 10;
    }

    doc.setFont('helvetica', 'italic');
    doc.setFontSize(10);
    doc.setTextColor(120);
    doc.text(footerNote, 105, y + 10, { align: 'center' });

    doc.save(`chat-export-${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  return (
    <Modal
      isOpen={isOpen}
      size="lg"
      centered
      backdrop="static"
      keyboard={false}
      toggle={handleCancel}>
      <ModalHeader
        toggle={handleCancel}
        className="d-flex justify-content-center position-relative fs-6">
        Chat Details
        {chatHistory?.length > 0 ? (
          <Icon.Download
            size={20}
            className="position-absolute text-info cursor-pointer"
            style={{
              right: '50px',
              top: '20px'
            }}
            onClick={() => handleExportPDF()}
          />
        ) : null}
      </ModalHeader>
      <ModalBody style={{ height: 'calc(100vh - 180px)' }}>
        <div className="chat-app__messages flex-grow-1 p-3 overflow-auto h-100">
          {chatHistory?.length > 0 ? (
            chatHistory.map((msg, idx, array) => {
              const isSentByCurrentUser = msg?.direction !== 'inbound';
              const formattedTime = format(new Date(msg.timestamp), 'hh:mm a');
              const extension = getFileExtension(msg?.media?.url || null);

              const currentDate = startOfDay(new Date(msg.timestamp));
              const nextDate = startOfDay(new Date(chatHistory[idx + 1]?.timestamp)) || null;

              const isNewDay = array?.length - 1 === idx ? true : !isEqual(currentDate, nextDate);

              let dateLabel = '';
              if (isNewDay) {
                if (isToday(currentDate)) {
                  dateLabel = 'Today';
                } else if (isYesterday(currentDate)) {
                  dateLabel = 'Yesterday';
                } else {
                  dateLabel = format(currentDate, 'dd MMMM yyyy');
                }
              }

              if (['transformed', 'Type', 'Status', 'assign'].includes(msg?.action)) {
                return (
                  <React.Fragment key={msg._id}>
                    <div className="d-flex justify-content-center align-items-center">
                      {msg?.action === 'transformed' || msg?.action === 'assign' ? (
                        <span className="assign-notification">
                          {`Ticket transformed${msg?.old_type !== 'system' ? ` from ${findUserName(Number(msg?.old_type))}` : ` from ${msg?.old_type}`} to ${msg?.new_type}`}
                        </span>
                      ) : msg?.action === 'Type' ? (
                        <span className="assign-notification">
                          {`Ticket Status changed ${msg?.old_type} to ${msg?.new_type}`}{' '}
                        </span>
                      ) : (
                        <span className="assign-notification">
                          {`Ticket Type changed ${msg?.old_type} to ${msg?.new_type}`}{' '}
                        </span>
                      )}
                    </div>
                    {dateLabel && (
                      <div className="d-flex justify-content-center align-items-center">
                        <span className="assign-notification" style={{ padding: '5px 20px' }}>
                          {dateLabel}
                        </span>
                      </div>
                    )}
                  </React.Fragment>
                );
              }
              return (
                <React.Fragment key={msg._id}>
                  <div
                    className={`chat-app__message-wrapper ${isSentByCurrentUser ? 'sent' : 'received'}`}>
                    <div
                      className={`chat-app__message ${msg?.media?.url ? 'chat-app__media' : ''}`}>
                      {msg?.media ? (
                        <div className="chat-app__message-text">
                          {extension === 'pdf' ? (
                            <div>
                              {msg?.fileName ? msg?.fileName : getFileName(msg?.media?.url)}
                              <div className="media-icon">
                                <Icon.FileText size={40} />
                              </div>
                              <div className="w-100 d-flex align-items-center gap-1">
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setIsPreviewUrl(msg?.media?.url)}>
                                    Open
                                  </Button>
                                </div>
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      fileDownload(msg?.media?.url);
                                    }}>
                                    Download
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div>
                              {msg?.fileName ? msg?.fileName : getFileName(msg?.media?.url)}
                              <div className="image-wrapper">
                                <img src={msg?.media?.url} />
                              </div>
                              <div className="w-100 d-flex align-items-center gap-1">
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setIsPreviewUrl(msg?.media?.url)}>
                                    Open
                                  </Button>
                                </div>
                                <div className="w-50">
                                  <Button
                                    className="w-100"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => {
                                      fileDownload(msg?.media?.url);
                                    }}>
                                    Download
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="chat-app__message-text">{msg.message}</div>
                      )}
                      <div className="chat-app__message-time">{formattedTime}</div>
                    </div>
                  </div>
                  {dateLabel && (
                    <div className="d-flex justify-content-center align-items-center">
                      <span className="assign-notification" style={{ padding: '5px 20px' }}>
                        {dateLabel}
                      </span>
                    </div>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            <div
              style={{
                height: 'calc(100vh - 250px)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center'
              }}>
              <span style={{ color: '#888', fontSize: '16px' }}>No history found</span>
            </div>
          )}
        </div>
      </ModalBody>
    </Modal>
  );
};

WhatsappReportInfo.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func,
  setIsPreviewUrl: PropTypes.func,
  details: PropTypes.object
};

export default WhatsappReportInfo;
